import { useState, useCallback, useRef } from 'react';
import { ChatMessage, AIExpert } from '@garbera/shared';
import { ChatError, MOCK_RESPONSES } from '../../types/message';
import { ChatState } from './types';
import axios from 'axios';
import { processFilesForUpload } from '../../utils/fileUtils';

/**
 * チャットメッセージの送受信を担当するフック
 */
export const useChatMessages = (enableMockResponses = false) => {
  const [state, setState] = useState<Pick<ChatState, 'messages' | 'isLoading' | 'isTyping' | 'error' | 'messageStatuses'>>({
    messages: [],
    isLoading: false,
    isTyping: false,
    error: null,
    messageStatuses: {},
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeRequestsRef = useRef<Map<string, AbortController>>(new Map());
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isAborting, setIsAborting] = useState(false);
  const [currentUserMessageId, setCurrentUserMessageId] = useState<number | null>(null);

  // リクエストID生成機能
  const generateRequestId = useCallback((): string => {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // アクティブなリクエストを管理
  const addActiveRequest = useCallback((requestId: string, abortController: AbortController) => {
    activeRequestsRef.current.set(requestId, abortController);
    setCurrentRequestId(requestId);
  }, []);

  const removeActiveRequest = useCallback((requestId: string) => {
    activeRequestsRef.current.delete(requestId);
    if (currentRequestId === requestId) {
      setCurrentRequestId(null);
    }
  }, [currentRequestId]);

  // メッセージ送信を中断する
  const abortMessage = useCallback(async (sessionId?: number): Promise<boolean> => {
    if (!currentRequestId) {
      console.warn('No active request to abort');
      return false;
    }

    setIsAborting(true);

    try {
      // フロントエンド側の中断
      const abortController = activeRequestsRef.current.get(currentRequestId);
      if (abortController) {
        abortController.abort();
        console.log('Frontend request aborted:', currentRequestId);
      }

      // バックエンド側の中断（セッションIDがある場合）
      if (sessionId) {
        try {
          const response = await axios.post(`/api/chat/sessions/${sessionId}/abort`, {
            requestId: currentRequestId
          }, {
            timeout: 5000 // 中断リクエストは短いタイムアウト
          });

          if (response.data.success) {
            console.log('Backend request aborted successfully:', currentRequestId);
          } else {
            console.warn('Backend abort failed:', response.data.error?.message);
          }
        } catch (error) {
          console.error('Failed to abort backend request:', error);
          // バックエンドの中断に失敗してもフロントエンドの中断は有効
        }
      }

      // 中断されたユーザーメッセージのステータスを更新
      if (currentUserMessageId) {
        const userMessage = state.messages.find(m => m.id === currentUserMessageId);
        setState(prev => ({
          ...prev,
          messageStatuses: {
            ...prev.messageStatuses,
            [currentUserMessageId]: {
              id: currentUserMessageId,
              status: 'aborted',
              timestamp: new Date().toISOString(),
              originalContent: userMessage?.content || '',
            }
          }
        }));
      }

      // 未完了のAIメッセージを削除（isTyping中のメッセージ）
      setState(prev => ({
        ...prev,
        // 送信中だったユーザーメッセージ以外の最新メッセージ（AIメッセージ）を削除
        messages: prev.messages.filter(m => {
          // 最後のメッセージがAI応答で、まだ生成中の場合は削除
          const isLastMessage = m.id === prev.messages[prev.messages.length - 1]?.id;
          const isAIMessage = m.messageType === 'assistant';
          return !(isLastMessage && isAIMessage && prev.isTyping);
        }),
        isLoading: false,
        isTyping: false,
        error: null, // エラーメッセージは表示しない
      }));

      // アクティブなリクエストをクリーンアップ
      removeActiveRequest(currentRequestId);
      setCurrentUserMessageId(null);

      return true;
    } catch (error) {
      console.error('Failed to abort message:', error);
      return false;
    } finally {
      setIsAborting(false);
    }
  }, [currentRequestId, currentUserMessageId, state.messages, removeActiveRequest]);

  const sendMessage = useCallback(async (
    content: string, 
    expert: AIExpert,
    sessionId?: number,
    files?: File[]
  ): Promise<ChatMessage | null> => {
    console.log('useChatMessages.sendMessage called with:', { content, expertName: expert.name, sessionId, filesCount: files?.length || 0 });
    
    if (!content.trim() && (!files || files.length === 0)) {
      setState(prev => ({
        ...prev,
        error: {
          type: 'validation',
          message: 'メッセージまたはファイルを入力してください',
          details: null,
        },
      }));
      return null;
    }

    // sessionIdが必要だが提供されていない場合のチェック（モック応答が無効の場合）
    if (!enableMockResponses && !sessionId) {
      setState(prev => ({
        ...prev,
        error: {
          type: 'validation',
          message: 'セッションIDが必要です',
          details: null,
        },
      }));
      return null;
    }

    // リクエストIDを生成
    const requestId = generateRequestId();
    const abortController = new AbortController();
    
    // アクティブなリクエストとして登録
    addActiveRequest(requestId, abortController);

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      isTyping: false,
    }));

    let userMessage: ChatMessage | null = null;

    try {
      // ファイル情報をBase64エンコード付きで変換
      const attachedFiles = files && files.length > 0 
        ? await processFilesForUpload(files)
        : undefined;

      // ユーザーメッセージを作成
      userMessage = {
        id: Date.now(),
        sessionId: sessionId || 0,
        userId: 1, // TODO: 実際のユーザーIDを使用
        content,
        attachedFiles, // Base64データ付きファイル情報を追加
        messageType: 'user',
        createdAt: new Date().toISOString(),
      };

      // アクティブなユーザーメッセージIDを設定
      setCurrentUserMessageId(userMessage.id);

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, userMessage!],
        messageStatuses: {
          ...prev.messageStatuses,
          [userMessage!.id]: {
            id: userMessage!.id,
            status: 'sending',
            timestamp: new Date().toISOString(),
          }
        }
      }));

      let aiMessage: ChatMessage;

      // モック応答か実際のAPI呼び出し
      if (enableMockResponses) {
        aiMessage = await generateMockResponse(userMessage, expert, abortController);
      } else {
        aiMessage = await sendToAPI(userMessage, expert, sessionId, files, requestId, abortController);
      }

      // 成功時にメッセージステータスを更新
      setState(prev => ({
        ...prev,
        messageStatuses: {
          ...prev.messageStatuses,
          [userMessage!.id]: {
            id: userMessage!.id,
            status: 'sent',
            timestamp: new Date().toISOString(),
          }
        }
      }));

      return aiMessage;
    } catch (error) {
      // 中断エラーの確実な検出（複数パターンに対応）
      const isAbortError = 
        (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) ||
        axios.isCancel(error) ||
        abortController?.signal.aborted;

      if (isAbortError) {
        console.log('Request was aborted by user');
        // 中断時はエラー表示を一切行わず、静かに終了
        return null;
      }

      // その他のエラー処理
      if (userMessage) {
        setState(prev => ({
          ...prev,
          messageStatuses: {
            ...prev.messageStatuses,
            [userMessage!.id]: {
              id: userMessage!.id,
              status: 'failed',
              timestamp: new Date().toISOString(),
              error: error instanceof Error ? error.message : 'Unknown error',
            }
          }
        }));
      }

      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: 'メッセージの送信に失敗しました',
          details: error,
        },
      }));
      return null;
    } finally {
      // クリーンアップ
      removeActiveRequest(requestId);
      setCurrentUserMessageId(null);
      setState(prev => ({
        ...prev,
        isLoading: false,
        isTyping: false,
      }));
    }
  }, [enableMockResponses, generateRequestId, addActiveRequest, removeActiveRequest]);

  const generateMockResponse = async (userMessage: ChatMessage, expert: AIExpert, abortController?: AbortController): Promise<ChatMessage> => {
    // タイピング状態
    setState(prev => ({ ...prev, isTyping: true }));

    // 中断可能な遅延をシミュレート
    const delayMs = 1000 + Math.random() * 2000;
    await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(resolve, delayMs);
      
      abortController?.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new Error('Request aborted'));
      });
    });

    // expertに基づいて適切なモック応答を選択
    const mockResponseKey = expert.name?.toLowerCase().includes('法') ? 'legal' : 'general';
    const mockResponse = MOCK_RESPONSES[mockResponseKey as keyof typeof MOCK_RESPONSES] || MOCK_RESPONSES.general;
    
    // MOCK_RESPONSESの構造を安全に処理
    let randomResponse: string;
    if (typeof mockResponse === 'string') {
      // 文字列の場合（旧構造）
      randomResponse = mockResponse;
    } else if (Array.isArray(mockResponse)) {
      // 文字列配列の場合（旧構造）
      randomResponse = mockResponse[Math.floor(Math.random() * mockResponse.length)];
    } else if (mockResponse && typeof mockResponse === 'object' && 'content' in mockResponse) {
      // オブジェクト構造の場合（現在の構造）
      randomResponse = mockResponse.content;
    } else {
      // フォールバック
      randomResponse = 'ご質問ありがとうございます。詳しく教えていただけますでしょうか？';
    }

    const aiMessage: ChatMessage = {
      id: Date.now() + 1,
      sessionId: userMessage.sessionId,
      userId: expert.id,
      content: randomResponse,
      attachedFiles: undefined, // AIメッセージには通常ファイル添付なし
      messageType: 'assistant',
      createdAt: new Date().toISOString(),
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, aiMessage],
      messageStatuses: {
        ...prev.messageStatuses,
        [aiMessage.id]: {
          id: aiMessage.id,
          status: 'sent',
          timestamp: new Date().toISOString(),
        }
      },
      isTyping: false,
    }));

    return aiMessage;
  };

  const sendToAPI = async (
    userMessage: ChatMessage,
    expert: AIExpert,
    sessionId?: number,
    files?: File[],
    requestId?: string,
    abortController?: AbortController
  ): Promise<ChatMessage> => {
    if (!sessionId) {
      throw new Error('Session ID is required for API calls');
    }

    console.log('sendToAPI called with:', { sessionId, content: userMessage.content, expert: expert.name, filesCount: files?.length || 0 });

    // AI応答待ち中のタイピング表示を開始
    setState(prev => ({ ...prev, isTyping: true }));

    try {
      // ファイル情報をBase64エンコード付きで変換
      const attachedFiles = files && files.length > 0 
        ? await processFilesForUpload(files)
        : undefined;

      // APIエンドポイントにメッセージを送信
      console.log('Sending API request to:', `/api/chat/sessions/${sessionId}/messages`);
      console.log('Request payload:', { 
        content: userMessage.content, 
        attachedFiles: attachedFiles?.map(f => ({ 
          id: f.id,
          fileName: f.fileName,
          fileSize: f.fileSize,
          fileType: f.fileType,
          hasBase64Data: !!f.base64Data
        }))
      });
      
      const response = await axios.post(`/api/chat/sessions/${sessionId}/messages`, {
        content: userMessage.content,
        attachedFiles, // Base64データ付きファイル情報
        stream: false, // 現在はストリーミングを無効化
        requestId: requestId // リクエストIDをバックエンドに送信
      }, {
        signal: abortController?.signal // AbortControllerのsignalを設定
      });

      console.log('API Response received:', response.data); // デバッグ用ログ

      if (response.data.success) {
        const { aiResponse } = response.data.data;
        
        // AIレスポンスの存在とプロパティをチェック
        if (!aiResponse) {
          throw new Error('AI応答が取得できませんでした');
        }
        
        if (!aiResponse.id || !aiResponse.content) {
          throw new Error('AI応答のデータが不完全です');
        }
        
        // AIの応答メッセージを作成
        const aiMessage: ChatMessage = {
          id: aiResponse.id,
          sessionId: sessionId,
          userId: expert.id,
          content: aiResponse.content,
          attachedFiles: aiResponse.attachedFiles || undefined, // バックエンドからのファイル情報
          messageType: 'assistant',
          createdAt: aiResponse.createdAt || new Date().toISOString(),
        };

        // メッセージリストに追加
        setState(prev => ({
          ...prev,
          messages: [...prev.messages, aiMessage],
        }));

        return aiMessage;
      } else {
        const errorMessage = response.data.error?.message || 'メッセージの送信に失敗しました';
        console.error('API Error:', response.data.error); // デバッグ用ログ
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('sendToAPI Error:', error); // デバッグ用ログ
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error?.message || 'サーバーエラーが発生しました';
        console.error('Axios Error Response:', error.response?.data); // デバッグ用ログ
        throw new Error(errorMessage);
      }
      throw error;
    }
  };

  const retryMessage = useCallback(async (messageId: number, expert: AIExpert, sessionId?: number) => {
    const message = state.messages.find(m => m.id === messageId);
    if (message && message.messageType === 'user') {
      return await sendMessage(message.content, expert, sessionId);
    }
    return null;
  }, [state.messages, sendMessage]);

  // やり直し機能の追加
  const retryAbortedMessage = useCallback(async (messageId: number, expert: AIExpert, sessionId?: number): Promise<ChatMessage | null> => {
    const messageStatus = state.messageStatuses[messageId];
    if (messageStatus?.status === 'aborted' && messageStatus.originalContent) {
      // 中断されたメッセージを削除
      setState(prev => {
        const { [messageId]: removed, ...remainingStatuses } = prev.messageStatuses;
        return {
          ...prev,
          messages: prev.messages.filter(m => m.id !== messageId),
          messageStatuses: remainingStatuses
        };
      });

      // 元のメッセージ内容で再送信
      return await sendMessage(messageStatus.originalContent, expert, sessionId);
    }
    return null;
  }, [state.messageStatuses, sendMessage]);

  // AIメッセージの再生成
  const regenerateAIMessage = useCallback(async (messageId: number, sessionId: number): Promise<ChatMessage | null> => {
    console.log('regenerateAIMessage called with:', { messageId, sessionId });
    
    // AIメッセージの存在確認
    const aiMessage = state.messages.find(m => m.id === messageId && m.messageType === 'assistant');
    if (!aiMessage) {
      console.error('AI message not found:', { messageId, availableMessages: state.messages.map(m => ({ id: m.id, type: m.messageType })) });
      setState(prev => ({
        ...prev,
        error: {
          type: 'validation',
          message: '再生成対象のAIメッセージが見つかりません',
          details: null,
        },
      }));
      return null;
    }

    console.log('Found AI message to regenerate:', aiMessage);

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      isTyping: true,
    }));

    try {
      console.log('Calling regenerate API:', `/api/chat/sessions/${sessionId}/regenerate`);
      console.log('Request payload:', { messageId, stream: false });
      
      const response = await axios.post(`/api/chat/sessions/${sessionId}/regenerate`, {
        messageId,
        stream: false
      });

      console.log('Regenerate API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response data structure:', {
        success: response.data.success,
        data: response.data.data,
        hasNewMessage: !!response.data.data?.newMessage,
        newMessageStructure: response.data.data?.newMessage ? Object.keys(response.data.data.newMessage) : 'none'
      });

      if (response.data.success) {
        const { aiResponse } = response.data.data;
        
        console.log('AI response from API:', aiResponse);
        
        if (!aiResponse) {
          console.error('No aiResponse in response:', response.data);
          throw new Error('新しいAI応答が取得できませんでした');
        }

        if (!aiResponse.content) {
          console.error('aiResponse has no content:', aiResponse);
          throw new Error('新しいAI応答の内容が空です');
        }

        console.log('Processing regenerated message:', {
          oldId: messageId,
          newId: aiResponse.id,
          content: aiResponse.content?.substring(0, 100) + '...'
        });

        // 古いAIメッセージを削除し、新しいメッセージを追加
        setState(prev => {
          const filteredMessages = prev.messages.filter(m => m.id !== messageId);
          
          const regeneratedMessage: ChatMessage = {
            id: aiResponse.id,
            sessionId: sessionId,
            userId: aiResponse.userId || aiMessage.userId,
            content: aiResponse.content,
            attachedFiles: aiResponse.attachedFiles || undefined, // バックエンドからのファイル情報
            messageType: 'assistant',
            createdAt: aiResponse.createdAt || new Date().toISOString(),
          };
          
          console.log('Adding regenerated message to state:', regeneratedMessage);
          
          return {
            ...prev,
            messages: [...filteredMessages, regeneratedMessage],
            messageStatuses: {
              ...prev.messageStatuses,
              [regeneratedMessage.id]: {
                id: regeneratedMessage.id,
                status: 'sent',
                timestamp: new Date().toISOString(),
              }
            },
            isLoading: false,
            isTyping: false,
          };
        });

        return {
          id: aiResponse.id,
          sessionId: sessionId,
          userId: aiResponse.userId || aiMessage.userId,
          content: aiResponse.content,
          attachedFiles: aiResponse.attachedFiles || undefined, // バックエンドからのファイル情報
          messageType: 'assistant',
          createdAt: aiResponse.createdAt || new Date().toISOString(),
        } as ChatMessage;
      } else {
        const errorMessage = response.data.error?.message || 'AIメッセージの再生成に失敗しました';
        console.error('API returned error:', response.data.error);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('regenerateAIMessage Error:', error);
      console.error('Error details:', {
        isAxiosError: axios.isAxiosError(error),
        response: error.response?.data,
        status: error.response?.status,
        message: error.message
      });
      
      let errorMessage = 'AIメッセージの再生成に失敗しました';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.error?.message || 'サーバーエラーが発生しました';
        console.error('Axios Error Response:', error.response?.data);
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: errorMessage,
          details: error,
        },
        isLoading: false,
        isTyping: false,
      }));
      
      return null;
    }
  }, [state.messages]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const clearMessages = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      messages: [], 
      messageStatuses: {},
      error: null 
    }));
  }, []);

  // メッセージ履歴を読み込む機能を追加
  const loadMessages = useCallback(async (sessionId: number): Promise<void> => {
    // 既に読み込み中の場合は重複実行を防ぐ
    setState(prev => {
      if (prev.isLoading) {
        console.log('Messages already loading, skipping...');
        return prev;
      }
      return { ...prev, isLoading: true, error: null };
    });

    try {
      console.log('Loading messages for session:', sessionId);
      const response = await axios.get(`/api/chat/sessions/${sessionId}/messages?limit=100&order=ASC`);

      console.log('Messages API Response:', response.data);

      if (response.data.success) {
        const messages = response.data.data?.messages || [];
        console.log('Loaded messages:', messages.length);

        // メッセージをChatMessage形式に変換
        const chatMessages: ChatMessage[] = messages.map((msg: any) => ({
          id: msg.id,
          sessionId: sessionId,
          userId: msg.user?.id || msg.userId || 0,
          content: msg.content,
          attachedFiles: msg.attachedFiles, // ファイル情報を含める
          messageType: msg.messageType,
          createdAt: msg.createdAt,
        }));

        setState(prev => ({
          ...prev,
          messages: chatMessages,
          isLoading: false,
        }));
      } else {
        const errorMessage = response.data.error?.message || 'メッセージの取得に失敗しました';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      
      let errorMessage = 'メッセージ履歴の読み込みに失敗しました';
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          errorMessage = 'チャットセッションが見つかりません';
        } else {
          errorMessage = error.response?.data?.error?.message || 'サーバーエラーが発生しました';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: errorMessage,
          details: error,
        },
        isLoading: false,
      }));
    }
  }, []);

  // 後方互換性のためのヘルパー関数
  const getMessageStatus = useCallback((messageId: number): string => {
    const status = state.messageStatuses[messageId];
    return status ? status.status : 'unknown';
  }, [state.messageStatuses]);

  const getMessageStatusObject = useCallback((messageId: number) => {
    return state.messageStatuses[messageId];
  }, [state.messageStatuses]);

  return {
    ...state,
    currentRequestId,
    isAborting,
    sendMessage,
    retryMessage,
    regenerateAIMessage,
    abortMessage,
    retryAbortedMessage,
    clearError,
    clearMessages,
    loadMessages,
    getMessageStatus,
    getMessageStatusObject,
  };
};